// src/components/dataSells/FileUploadSection.jsx
import React, { useState } from 'react';
import { FaUpload, FaFileAlt, FaTimes, FaCheckCircle } from 'react-icons/fa';

/**
 * Componente para subir archivos CSV/Excel
 */
const FileUploadSection = ({ marcas, tiendas, onUpload, loading }) => {
    const [selectedFile, setSelectedFile] = useState(null);
    const [selectedMarca, setSelectedMarca] = useState('');
    const [selectedTiendas, setSelectedTiendas] = useState([]);
    const [dragActive, setDragActive] = useState(false);

    // Filtrar tiendas por marca seleccionada
    const tiendasFiltradas = selectedMarca
        ? tiendas.filter(t => t.marca === selectedMarca || t.marca._id === selectedMarca)
        : [];

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        validateAndSetFile(file);
    };

    const validateAndSetFile = (file) => {
        if (!file) return;

        // Validar tipo de archivo
        const allowedTypes = [
            'text/csv',
            'application/vnd.ms-excel',
            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
            'application/csv'
        ];
        const allowedExtensions = ['.csv', '.xlsx', '.xls'];
        const fileExtension = file.name.substring(file.name.lastIndexOf('.')).toLowerCase();

        if (!allowedTypes.includes(file.type) && !allowedExtensions.includes(fileExtension)) {
            alert('Solo se permiten archivos CSV o Excel (.csv, .xlsx, .xls)');
            return;
        }

        // Validar tamaño (10 MB)
        if (file.size > 10 * 1024 * 1024) {
            alert('El archivo es demasiado grande. Tamaño máximo: 10MB');
            return;
        }

        setSelectedFile(file);
    };

    const handleDrag = (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (e.type === 'dragenter' || e.type === 'dragover') {
            setDragActive(true);
        } else if (e.type === 'dragleave') {
            setDragActive(false);
        }
    };

    const handleDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setDragActive(false);

        if (e.dataTransfer.files && e.dataTransfer.files[0]) {
            validateAndSetFile(e.dataTransfer.files[0]);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!selectedFile) {
            alert('Por favor selecciona un archivo');
            return;
        }

        if (!selectedMarca) {
            alert('Por favor selecciona una marca');
            return;
        }

        if (selectedTiendas.length === 0) {
            alert('Por favor selecciona al menos una tienda');
            return;
        }

        await onUpload(selectedFile, selectedMarca, selectedTiendas);

        // Limpiar formulario
        setSelectedFile(null);
        setSelectedMarca('');
        setSelectedTiendas([]);
    };

    const handleTiendaToggle = (tiendaId) => {
        setSelectedTiendas(prev => {
            if (prev.includes(tiendaId)) {
                return prev.filter(id => id !== tiendaId);
            } else {
                return [...prev, tiendaId];
            }
        });
    };

    const formatFileSize = (bytes) => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
    };

    return (
        <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-xl font-semibold mb-4 flex items-center">
                <FaUpload className="mr-2 text-blue-500" />
                Subir Archivo de Ventas
            </h2>

            {/* Información de Procesamiento Automático */}
            <div className="mb-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h3 className="font-semibold text-blue-800 mb-2 flex items-center">
                    <FaCheckCircle className="mr-2" />
                    Procesamiento Automático Inteligente
                </h3>
                <p className="text-sm text-blue-700 mb-2">
                    Una vez que subas tu archivo, el sistema procesará automáticamente en segundo plano:
                </p>
                <ol className="text-sm text-blue-700 space-y-1 ml-4 list-decimal">
                    <li><strong>Validación:</strong> Verificamos el formato y estructura del archivo</li>
                    <li><strong>Mapeo con IA:</strong> Nuestro sistema identifica automáticamente los campos</li>
                    <li><strong>Procesamiento:</strong> Importamos todas las ventas detectando duplicados</li>
                    <li><strong>Enriquecimiento:</strong> Creamos productos faltantes y comparamos con tickets</li>
                </ol>
                <p className="text-sm text-blue-700 mt-2">
                    💡 <em>Recibirás notificaciones del progreso. Si algún campo no se puede mapear automáticamente, te pediremos que lo completes manualmente.</em>
                </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
                {/* Seleccionar Marca */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                        Marca *
                    </label>
                    <select
                        value={selectedMarca}
                        onChange={(e) => {
                            setSelectedMarca(e.target.value);
                            setSelectedTiendas([]); // Resetear tiendas al cambiar marca
                        }}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        disabled={loading}
                        required
                    >
                        <option value="">Selecciona una marca</option>
                        {marcas.map(marca => (
                            <option key={marca._id} value={marca._id}>
                                {marca.nombre}
                            </option>
                        ))}
                    </select>
                </div>

                {/* Seleccionar Tiendas */}
                {selectedMarca && (
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Tiendas * (selecciona al menos una)
                        </label>
                        <div className="border border-gray-300 rounded-lg p-3 max-h-40 overflow-y-auto">
                            {tiendasFiltradas.length === 0 ? (
                                <p className="text-gray-500 text-sm">No hay tiendas disponibles para esta marca</p>
                            ) : (
                                <div className="space-y-2">
                                    {tiendasFiltradas.map(tienda => (
                                        <label key={tienda._id} className="flex items-center cursor-pointer hover:bg-gray-50 p-2 rounded">
                                            <input
                                                type="checkbox"
                                                checked={selectedTiendas.includes(tienda._id)}
                                                onChange={() => handleTiendaToggle(tienda._id)}
                                                className="mr-2"
                                                disabled={loading}
                                            />
                                            <span className="text-sm">{tienda.nombre}</span>
                                        </label>
                                    ))}
                                </div>
                            )}
                        </div>
                        {selectedTiendas.length > 0 && (
                            <p className="text-sm text-gray-600 mt-2">
                                {selectedTiendas.length} tienda(s) seleccionada(s)
                            </p>
                        )}
                    </div>
                )}

                {/* Drag & Drop Zone */}
                <div
                    className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${dragActive
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-300 hover:border-gray-400'
                        }`}
                    onDragEnter={handleDrag}
                    onDragLeave={handleDrag}
                    onDragOver={handleDrag}
                    onDrop={handleDrop}
                >
                    {selectedFile ? (
                        <div className="flex items-center justify-center space-x-3">
                            <FaCheckCircle className="text-green-500 text-2xl" />
                            <div className="text-left">
                                <p className="font-medium text-gray-700">{selectedFile.name}</p>
                                <p className="text-sm text-gray-500">{formatFileSize(selectedFile.size)}</p>
                            </div>
                            <button
                                type="button"
                                onClick={() => setSelectedFile(null)}
                                className="text-red-500 hover:text-red-700"
                                disabled={loading}
                            >
                                <FaTimes />
                            </button>
                        </div>
                    ) : (
                        <>
                            <FaFileAlt className="mx-auto text-4xl text-gray-400 mb-3" />
                            <p className="text-gray-600 mb-2">
                                Arrastra y suelta tu archivo aquí
                            </p>
                            <p className="text-gray-500 text-sm mb-3">o</p>
                            <label className="inline-block px-4 py-2 bg-blue-500 text-white rounded-lg cursor-pointer hover:bg-blue-600 transition-colors">
                                Seleccionar archivo
                                <input
                                    type="file"
                                    onChange={handleFileChange}
                                    accept=".csv,.xlsx,.xls"
                                    className="hidden"
                                    disabled={loading}
                                />
                            </label>
                            <p className="text-xs text-gray-500 mt-3">
                                CSV o Excel (.csv, .xlsx, .xls) • Máximo 10MB
                            </p>
                        </>
                    )}
                </div>

                {/* Botón de Submit */}
                <button
                    type="submit"
                    disabled={loading || !selectedFile || !selectedMarca || selectedTiendas.length === 0}
                    className="w-full px-4 py-3 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors font-medium"
                >
                    {loading ? (
                        <span className="flex items-center justify-center">
                            <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                            Subiendo...
                        </span>
                    ) : (
                        <>Subir Archivo</>
                    )}
                </button>
            </form>
        </div>
    );
};

export default FileUploadSection;

